import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { Subscription } from 'rxjs';
import { mapWorkflowRegistryDiagnosticError } from './platform-workflow-registry-diagnostics.models';
import type {
  QuorumAbstentionRule,
  QuorumApprovalRule,
  QuorumBallotChoice,
  QuorumDenominator,
  QuorumTieRule,
  WorkflowQuorumCalculationRequest,
  WorkflowQuorumCalculationResult,
  WorkflowQuorumRules,
} from './workflow-quorum.models';
import { WorkflowQuorumService } from './workflow-quorum.service';

interface SampleMember {
  name: string;
  choice: QuorumBallotChoice | 'missing';
}

@Component({
  selector: 'phms-workflow-quorum-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective],
  templateUrl: './workflow-quorum-calculator.component.html',
  styleUrl: './workflow-quorum-calculator.component.scss',
})
export class WorkflowQuorumCalculatorComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() disabled = false;
  private readonly api = inject(WorkflowQuorumService);
  private rulesRequest?: Subscription;
  private calculationRequest?: Subscription;
  private rulesGeneration = 0;
  private calculationGeneration = 0;
  rules: WorkflowQuorumRules | null = null;
  rulesLoading = false;
  rulesError = '';
  loading = false;
  error = '';
  result: WorkflowQuorumCalculationResult | null = null;
  quorumThreshold: number | null = null;
  approvalRuleCode: QuorumApprovalRule | '' = '';
  denominatorCode: QuorumDenominator | '' = '';
  abstentionQuorumCode: QuorumAbstentionRule | '' = '';
  tieCode: QuorumTieRule | '' = '';
  members: SampleMember[] = [{ name: '', choice: 'missing' }];

  ngOnInit(): void {
    this.loadRules();
  }

  ngOnChanges(): void {
    if (this.disabled) this.inputsChanged();
  }

  ngOnDestroy(): void {
    this.rulesGeneration++;
    this.calculationGeneration++;
    this.rulesRequest?.unsubscribe();
    this.calculationRequest?.unsubscribe();
  }

  get controlsDisabled(): boolean {
    return this.disabled || this.rulesLoading || !this.rules;
  }

  loadRules(): void {
    const generation = ++this.rulesGeneration;
    this.rulesRequest?.unsubscribe();
    this.inputsChanged();
    this.rulesLoading = true;
    this.rulesError = '';
    this.rules = null;
    this.rulesRequest = this.api.getRules().subscribe({
      next: (rules) => {
        if (generation !== this.rulesGeneration) return;
        this.rules = rules;
        this.rulesLoading = false;
      },
      error: (error: unknown) => {
        if (generation !== this.rulesGeneration) return;
        this.rulesLoading = false;
        this.rulesError = this.errorMessage(error);
      },
    });
  }

  inputsChanged(): void {
    this.calculationGeneration++;
    this.calculationRequest?.unsubscribe();
    this.loading = false;
    this.result = null;
    this.error = '';
  }

  loadExample(): void {
    if (this.controlsDisabled) return;
    this.inputsChanged();
    this.quorumThreshold = 3;
    this.approvalRuleCode = 'strict_majority';
    this.denominatorCode = 'votes_cast';
    this.abstentionQuorumCode = 'count';
    this.tieCode = 'no_decision';
    this.members = [
      { name: 'Alex', choice: 'approve' },
      { name: 'Blair', choice: 'approve' },
      { name: 'Casey', choice: 'reject' },
      { name: 'Drew', choice: 'abstain' },
      { name: 'Erin', choice: 'missing' },
    ];
  }

  addMember(): void {
    if (
      this.controlsDisabled ||
      this.members.length >= this.rules!.maximumMembers
    )
      return;
    this.inputsChanged();
    this.members = [...this.members, { name: '', choice: 'missing' }];
  }

  removeMember(index: number): void {
    if (this.controlsDisabled || this.members.length <= 1) return;
    this.inputsChanged();
    this.members = this.members.filter((_, candidate) => candidate !== index);
  }

  calculate(): void {
    if (this.controlsDisabled || this.loading) return;
    this.inputsChanged();
    const request = this.createRequest();
    if (!request) return;
    const generation = ++this.calculationGeneration;
    this.loading = true;
    this.calculationRequest = this.api.calculate(request).subscribe({
      next: (result) => {
        if (generation !== this.calculationGeneration) return;
        this.loading = false;
        this.result = result;
      },
      error: (error: unknown) => {
        if (generation !== this.calculationGeneration) return;
        this.loading = false;
        this.error = this.errorMessage(error);
      },
    });
  }

  get outcomeLabel(): string {
    switch (this.result?.outcomeCode) {
      case 'approved':
        return 'Approval threshold met';
      case 'rejected':
        return 'Approval threshold not met';
      case 'no_decision':
        return 'No decision in this sample';
      case 'quorum_not_met':
        return 'Quorum not met';
      default:
        return '';
    }
  }

  reasonLabel(reason: string): string {
    const labels: Record<string, string> = {
      'quorum.not_met': 'Too few members count toward the configured quorum.',
      'quorum.no_decisive_votes':
        'No approval or rejection votes were supplied.',
      'quorum.tie_no_decision':
        'The votes are tied; the selected tie rule gives no decision.',
      'quorum.tie_rejected':
        'The votes are tied; the selected tie rule rejects this sample.',
      'quorum.approval_threshold_met':
        'The approval votes meet the selected threshold.',
      'quorum.approval_threshold_not_met':
        'The approval votes do not meet the selected threshold.',
    };
    return (
      labels[reason] ?? 'The calculation returned an additional result reason.'
    );
  }

  private createRequest(): WorkflowQuorumCalculationRequest | null {
    const rules = this.rules!;
    const approvalRuleCode = this.approvalRuleCode;
    const denominatorCode = this.denominatorCode;
    const abstentionQuorumCode = this.abstentionQuorumCode;
    const tieCode = this.tieCode;
    if (
      !approvalRuleCode ||
      !denominatorCode ||
      !abstentionQuorumCode ||
      !tieCode ||
      !rules.approvalRules.some((rule) => rule.value === approvalRuleCode) ||
      !rules.denominators.some((rule) => rule.value === denominatorCode) ||
      !rules.abstentionQuorumRules.some(
        (rule) => rule.value === abstentionQuorumCode,
      ) ||
      !rules.tieRules.some((rule) => rule.value === tieCode)
    ) {
      this.error =
        'Choose an approval rule, denominator, abstention rule and tie rule.';
      return null;
    }
    if (
      !Number.isInteger(this.quorumThreshold) ||
      !this.quorumThreshold ||
      this.quorumThreshold < 1 ||
      this.quorumThreshold > rules.maximumMembers
    ) {
      this.error = `Enter a whole-number quorum threshold from 1 to ${rules.maximumMembers}.`;
      return null;
    }
    const names = this.members.map((member) => member.name.trim());
    if (
      !names.length ||
      names.length > rules.maximumMembers ||
      names.some((name) => !name)
    ) {
      this.error = `Name each sample member. Use 1 to ${rules.maximumMembers} members.`;
      return null;
    }
    if (new Set(names).size !== names.length) {
      this.error = 'Use a distinct name for each sample member.';
      return null;
    }
    if (
      this.members.some(
        (member) =>
          !['approve', 'reject', 'abstain', 'missing'].includes(member.choice),
      )
    ) {
      this.error = 'Choose a supported vote or Missing for each sample member.';
      return null;
    }
    return {
      schemaVersion: rules.schemaVersion,
      ruleCode: rules.ruleCode,
      ruleVersion: rules.ruleVersion,
      settings: {
        quorumThreshold: this.quorumThreshold,
        approvalRuleCode,
        denominatorCode,
        abstentionQuorumCode,
        tieCode,
      },
      members: names,
      ballots: this.members.flatMap((member, index) =>
        member.choice === 'missing'
          ? []
          : [{ memberKey: names[index], choice: member.choice }],
      ),
    };
  }

  private errorMessage(error: unknown): string {
    const display = mapWorkflowRegistryDiagnosticError(error);
    return `${display.title}: ${display.messages.join(' ')}`;
  }
}
